import { useCallback, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { OrderDirection } from 'state/kodiakIslands/types'
import { KodiakFarm } from 'types/farms'

import {
  setKodiakFarmPagination as setFarmPaginationAction,
  setSearchTerm as setFarmSearchTermAction,
  setSortBy as setFarmSortByAction,
  updateKodiakFarms,
} from './reducer'
import { KodiakFarmOrderBy } from './types'

/**
 * All farms from the store
 */
export function useKodiakFarms(): KodiakFarm[] {
  const farms = useAppSelector((s) => s.kodiakFarms.kodiakFarms)
  return useMemo(() => farms, [farms])
}

/**
 * Updater: write farms array into the store
 */
export function useUpdateKodiakFarms(): (farms?: KodiakFarm[]) => void {
  const dispatch = useAppDispatch()
  return useCallback(
    (farms?: KodiakFarm[]) => {
      if (!farms || farms.length === 0) return
      dispatch(updateKodiakFarms({ kodiakFarms: farms }))
    },
    [dispatch]
  )
}

/**
 * Search term (getter + setter)
 */
export function useSearchTerm(): string {
  return useAppSelector((s) => s.kodiakFarms.searchTerm)
}
export function useSetFarmSearchTerm(): (search: string) => void {
  const dispatch = useAppDispatch()
  return useCallback((search: string) => dispatch(setFarmSearchTermAction(search)), [dispatch])
}

/**
 * Sort (getter + setter)
 */
export function useFarmSort(): { orderBy: KodiakFarmOrderBy; orderDirection: OrderDirection } {
  return useAppSelector((s) => ({
    orderBy: s.kodiakFarms.orderBy,
    orderDirection: s.kodiakFarms.orderDirection,
  }))
}
export function useSetFarmSort(): (args: { orderBy: KodiakFarmOrderBy; orderDirection: OrderDirection }) => void {
  const dispatch = useAppDispatch()
  return useCallback(
    (args: { orderBy: KodiakFarmOrderBy; orderDirection: OrderDirection }) => dispatch(setFarmSortByAction(args)),
    [dispatch]
  )
}

/**
 * Pagination (getter + setter)
 */
export function useFarmPagination(): { offset: number; limit: number } {
  return useAppSelector((s) => ({ offset: s.kodiakFarms.offset, limit: s.kodiakFarms.limit }))
}
export function useSetFarmPagination(): (args: { offset?: number; limit?: number }) => void {
  const dispatch = useAppDispatch()
  return useCallback((args: { offset?: number; limit?: number }) => dispatch(setFarmPaginationAction(args)), [dispatch])
}

/**
 * Transitions / loading flags
 */
export function useFarmTransitions(): {
  isLoading: boolean
  isDataTransition: boolean
  transitionType: 'sort' | 'filter' | 'search' | null
} {
  return useAppSelector((s) => ({
    isLoading: s.kodiakFarms.isLoading,
    isDataTransition: s.kodiakFarms.dataTransition,
    transitionType: s.kodiakFarms.transitionType,
  }))
}

/**
 * Handy bundle for building query args
 */
export function useFarmQueryArgs() {
  return useAppSelector((s) => {
    const st = s.kodiakFarms
    return {
      search: st.searchTerm,
      orderBy: st.orderBy,
      orderDirection: st.orderDirection,
      offset: st.offset,
      limit: st.limit,
    }
  })
}
